/**
 * Whether a message body is already HTML, or is plain text whose newlines are
 * the author's own line breaks.
 *
 * This one question is asked on both sides of the wire and the two sides must
 * never disagree:
 *
 * - the API asks it when **sending** — plain-text newlines have to become `<br>`
 *   before an HTML e-mail body (or a LinkedIn message) is assembled, otherwise
 *   they are silently dropped in transit (PREC-3954);
 * - the web app asks it when **displaying** — a plain-text body assigned to
 *   `innerHTML` renders as one continuous line unless its newlines are turned
 *   into `<br>` first (PREC-3956).
 *
 * It lives in `@signalist/shared-types` because that is the only module both
 * apps may import: the architecture gate forbids `apps/web` from reaching into
 * `apps/api`. A second copy of the regex in the web app would be free to drift,
 * which is exactly how the two "replied" predicates diverged in PREC-3909 and
 * PREC-3949.
 */

/**
 * Block-level tags whose presence means the body is already HTML.
 *
 * Deliberately block-level only. Inline tags do not qualify: a hand-typed
 * message that happens to carry a `<b>` or an `<a href>` — which is what lands
 * in the composer when a template made from an e-mail body is inserted — is
 * still plain text as far as its newlines are concerned, and treating it as
 * HTML would drop them.
 */
export const HTML_BLOCK_TAG_REGEX = /<(?:p|div|br|ul|ol|li|h[1-6]|table|blockquote)[\s>/]/i;

/** True when `text` carries block-level HTML, i.e. it is a document, not typed text. */
export function containsHtmlBlockTags(text: string): boolean {
	return HTML_BLOCK_TAG_REGEX.test(text);
}
